import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Mail, FileText, Plus, Trash2, Pencil, Save, X, Eye, FileDown } from "lucide-react";
import { api, SITE } from "@/lib/site";
import { toast } from "sonner";

export default function AdminDashboard() {
  const [tab, setTab] = useState("contacts");
  const [me, setMe] = useState(null);
  const [authChecking, setAuthChecking] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("asahni_admin_token");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    api
      .get("/admin/me")
      .then((r) => setMe(r.data))
      .catch(() => {
        localStorage.removeItem("asahni_admin_token");
        navigate("/admin/login");
      })
      .finally(() => setAuthChecking(false));
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("asahni_admin_token");
    navigate("/admin/login");
  };

  if (authChecking) {
    return (
      <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="admin-dashboard">
      <header className="border-b border-white/10 glass sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-md bg-gradient-to-br from-blue-500 to-cyan-400 grid place-items-center font-display font-extrabold text-black text-xs">
              {SITE.initials}
            </div>
            <div>
              <div className="font-display font-bold text-white text-sm">Admin Console</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                {me?.email}
              </div>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white px-3 py-1.5 rounded-full border border-white/10 hover:border-white/30"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-center gap-2 mb-8 border-b border-white/10">
          <TabButton active={tab === "contacts"} onClick={() => setTab("contacts")} icon={Mail}>
            Inquiries
          </TabButton>
          <TabButton active={tab === "blog"} onClick={() => setTab("blog")} icon={FileText}>
            Blog
          </TabButton>
          <TabButton active={tab === "resources"} onClick={() => setTab("resources")} icon={FileDown}>
            Resources
          </TabButton>
        </div>

        {tab === "contacts" ? <ContactsPanel /> : tab === "blog" ? <BlogPanel /> : <ResourcesPanel />}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={`tab-${String(children).toLowerCase()}`}
      className={`relative inline-flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
        active ? "text-white" : "text-slate-500 hover:text-slate-300"
      }`}
    >
      <Icon size={15} /> {children}
      {active && <span className="absolute left-0 right-0 -bottom-px h-px bg-blue-500" />}
    </button>
  );
}

function ContactsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = () => {
    setLoading(true);
    api
      .get("/admin/contacts")
      .then((r) => setItems(r.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try {
      await api.delete(`/admin/contacts/${id}`);
      setItems((x) => x.filter((i) => i.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Deleted");
    } catch {
      toast.error("Failed");
    }
  };

  const markRead = async (id, read) => {
    try {
      await api.patch(`/admin/contacts/${id}?read=${read}`);
      setItems((x) => x.map((i) => (i.id === id ? { ...i, read } : i)));
    } catch {}
  };

  return (
    <div data-testid="contacts-panel" className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <div className="lg:col-span-7 bg-[#0A0C13] border border-white/10 rounded-2xl">
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <div className="font-display font-semibold text-white">Inquiries ({items.length})</div>
          <button onClick={load} className="text-xs text-slate-400 hover:text-white">Refresh</button>
        </div>
        {loading ? (
          <div className="p-8 text-slate-500 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-slate-500 text-sm">No inquiries yet.</div>
        ) : (
          <ul className="divide-y divide-white/5">
            {items.map((c) => (
              <li
                key={c.id}
                onClick={() => setSelected(c)}
                className={`px-6 py-4 cursor-pointer hover:bg-white/5 ${
                  selected?.id === c.id ? "bg-white/[0.03]" : ""
                }`}
                data-testid={`contact-row-${c.id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-white text-sm">
                      {c.name}{" "}
                      {!c.read && (
                        <span className="ml-2 inline-block h-1.5 w-1.5 rounded-full bg-blue-400" />
                      )}
                    </div>
                    <div className="text-xs text-slate-500">
                      {c.email} · {c.company || "—"}
                    </div>
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                    {new Date(c.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="mt-1 text-xs text-slate-400 truncate">{c.program}</div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="lg:col-span-5 bg-[#0A0C13] border border-white/10 rounded-2xl p-6">
        {!selected ? (
          <div className="text-slate-500 text-sm h-full grid place-items-center">
            Select an inquiry to read
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display font-semibold text-white text-lg">{selected.name}</div>
                <div className="text-xs text-slate-500">{selected.email}</div>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => markRead(selected.id, !selected.read)}
                  className="text-xs px-2.5 py-1 rounded-full border border-white/10 text-slate-300 hover:text-white"
                >
                  Mark {selected.read ? "unread" : "read"}
                </button>
                <button
                  onClick={() => remove(selected.id)}
                  className="text-xs px-2.5 py-1 rounded-full border border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                  data-testid="delete-contact"
                >
                  <Trash2 size={12} className="inline -mt-0.5" /> Delete
                </button>
              </div>
            </div>
            <Row k="Company" v={selected.company} />
            <Row k="Phone" v={selected.phone} />
            <Row k="Program" v={selected.program} />
            <Row k="Received" v={new Date(selected.created_at).toLocaleString()} />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-2">Message</div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-sm text-slate-200 whitespace-pre-wrap">
                {selected.message}
              </div>
            </div>
            <a
              href={`mailto:${selected.email}?subject=Re: Your inquiry on aksahni.com`}
              className="inline-flex items-center gap-2 mt-2 text-sm text-blue-300 hover:text-blue-200"
            >
              <Mail size={14} /> Reply via email
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="grid grid-cols-3 gap-3 text-sm">
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">{k}</div>
      <div className="col-span-2 text-slate-200">{v || "—"}</div>
    </div>
  );
}

function BlogPanel() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null); // null | {id?, title, excerpt, content, cover_image, tags, published}
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api
      .get("/admin/blog")
      .then((r) => setPosts(r.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const create = () =>
    setEditing({
      title: "",
      excerpt: "",
      content: "",
      cover_image: "",
      tags: [],
      published: true,
    });

  const save = async () => {
    if (!editing.title || editing.title.length < 3) {
      toast.error("Title is too short");
      return;
    }
    if (editing.excerpt.length < 10 || editing.content.length < 20) {
      toast.error("Excerpt or content too short");
      return;
    }
    try {
      const payload = {
        title: editing.title,
        excerpt: editing.excerpt,
        content: editing.content,
        cover_image: editing.cover_image || null,
        tags: typeof editing.tags === "string"
          ? editing.tags.split(",").map((t) => t.trim()).filter(Boolean)
          : editing.tags,
        published: editing.published,
      };
      if (editing.id) {
        await api.put(`/admin/blog/${editing.id}`, payload);
        toast.success("Post updated");
      } else {
        await api.post("/admin/blog", payload);
        toast.success("Post created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/admin/blog/${id}`);
    setPosts((x) => x.filter((i) => i.id !== id));
    toast.success("Deleted");
  };

  if (editing) {
    return (
      <div data-testid="blog-editor" className="bg-[#0A0C13] border border-white/10 rounded-2xl p-7 space-y-4 max-w-3xl">
        <div className="flex items-center justify-between">
          <div className="font-display font-semibold text-white text-lg">
            {editing.id ? "Edit post" : "New post"}
          </div>
          <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-white">
            <X size={18} />
          </button>
        </div>

        <Input label="Title" value={editing.title}
          onChange={(v) => setEditing((e) => ({ ...e, title: v }))} testid="blog-title" />
        <Input label="Excerpt" value={editing.excerpt}
          onChange={(v) => setEditing((e) => ({ ...e, excerpt: v }))} testid="blog-excerpt" />
        <Input label="Cover image URL" value={editing.cover_image}
          onChange={(v) => setEditing((e) => ({ ...e, cover_image: v }))} testid="blog-cover" />
        <Input label="Tags (comma-separated)"
          value={Array.isArray(editing.tags) ? editing.tags.join(", ") : editing.tags}
          onChange={(v) => setEditing((e) => ({ ...e, tags: v }))} testid="blog-tags" />
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">Content</label>
          <textarea
            rows={12}
            value={editing.content}
            onChange={(e) => setEditing((s) => ({ ...s, content: e.target.value }))}
            data-testid="blog-content"
            className="mt-2 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/60 font-body text-sm leading-relaxed"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-300">
          <input
            type="checkbox"
            checked={editing.published}
            onChange={(e) => setEditing((s) => ({ ...s, published: e.target.checked }))}
            data-testid="blog-published"
          />
          Published
        </label>
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={() => setEditing(null)}
            className="px-5 py-2.5 rounded-full border border-white/10 text-slate-300 hover:text-white"
          >
            Cancel
          </button>
          <button
            onClick={save}
            data-testid="blog-save"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-black font-medium hover:bg-slate-200"
          >
            <Save size={14} /> Save
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="blog-panel">
      <div className="flex items-center justify-between mb-6">
        <div className="font-display font-semibold text-white text-lg">Posts ({posts.length})</div>
        <button
          onClick={create}
          data-testid="new-post-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-black font-medium hover:bg-slate-200"
        >
          <Plus size={14} /> New post
        </button>
      </div>
      {loading ? (
        <div className="text-slate-500 text-sm">Loading…</div>
      ) : posts.length === 0 ? (
        <div className="text-slate-500 text-sm">No posts yet.</div>
      ) : (
        <div className="bg-[#0A0C13] border border-white/10 rounded-2xl divide-y divide-white/5">
          {posts.map((p) => (
            <div
              key={p.id}
              data-testid={`post-row-${p.id}`}
              className="px-6 py-4 flex items-center justify-between gap-3"
            >
              <div className="min-w-0">
                <div className="font-medium text-white text-sm truncate">{p.title}</div>
                <div className="text-xs text-slate-500 truncate">/{p.slug}</div>
              </div>
              <div className="flex items-center gap-1.5">
                <a
                  href={`/blog/${p.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs px-2.5 py-1 rounded-full border border-white/10 text-slate-300 hover:text-white"
                >
                  <Eye size={12} className="inline -mt-0.5" /> View
                </a>
                <button
                  onClick={() => setEditing({ ...p, tags: p.tags?.join(", ") || "" })}
                  className="text-xs px-2.5 py-1 rounded-full border border-white/10 text-slate-300 hover:text-white"
                >
                  <Pencil size={12} className="inline -mt-0.5" /> Edit
                </button>
                <button
                  onClick={() => remove(p.id)}
                  className="text-xs px-2.5 py-1 rounded-full border border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                >
                  <Trash2 size={12} className="inline -mt-0.5" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Input({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">{label}</label>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-2 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/60"
      />
    </div>
  );
}


function ResourcesPanel() {
  const [items, setItems] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/resource-leads").then((r) => setItems(r.data || [])),
      api.get("/admin/resource-leads/analytics").then((r) => setAnalytics(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await api.delete(`/admin/resource-leads/${id}`);
    setItems((x) => x.filter((i) => i.id !== id));
    toast.success("Deleted");
  };

  const csv = () => {
    const header = "Name,Email,Role,Company,Resource,Source,Medium,Campaign,Referrer,Created\n";
    const rows = items
      .map((i) =>
        [
          i.name,
          i.email,
          i.role || "",
          i.company || "",
          i.resource,
          i.utm?.source || "",
          i.utm?.medium || "",
          i.utm?.campaign || "",
          i.utm?.referrer || "",
          i.created_at,
        ]
          .map((v) => `"${String(v).replace(/"/g, '""')}"`)
          .join(",")
      )
      .join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `asahni-resource-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const resourceLabel = (k) =>
    k === "entra-id-hardening-checklist" ? "Entra ID Checklist"
    : k === "az-500-exam-cheatsheet" ? "AZ-500 Cheatsheet"
    : k === "cissp-domain-mastery" ? "CISSP Cheatsheet"
    : k;

  const [preview, setPreview] = useState(null); // {leadId, day, subject, html}

  const previewNurture = async (leadId, day) => {
    try {
      const r = await api.get(`/admin/resource-leads/${leadId}/preview-nurture?day=${day}`);
      setPreview({ leadId, day, ...r.data });
    } catch {
      toast.error("Preview failed");
    }
  };

  const sendNurtureNow = async (leadId, day) => {
    if (!window.confirm(`Send Day-${day} email to this lead now?`)) return;
    try {
      const r = await api.post(`/admin/resource-leads/${leadId}/send-nurture?day=${day}`);
      if (r.data.ok) toast.success(`Day-${day} email sent`);
      else toast.error(`Day-${day} delivery failed (check Resend domain)`);
      load();
    } catch {
      toast.error("Send failed");
    }
  };

  return (
    <div data-testid="resources-panel">
      {/* Analytics row */}
      {analytics && analytics.total > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Stat label="Total downloads" value={analytics.total} testid="stat-total" />
          <Breakdown
            label="By resource"
            entries={Object.entries(analytics.by_resource || {}).map(([k, v]) => [resourceLabel(k), v])}
            testid="stat-by-resource"
          />
          <Breakdown
            label="By traffic source"
            entries={Object.entries(analytics.by_source || {})}
            testid="stat-by-source"
          />
        </div>
      )}

      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="font-display font-semibold text-white text-lg">
            Resource leads ({items.length})
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Visitors who requested a free resource — track which campaigns convert
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="text-xs text-slate-400 hover:text-white px-3 py-1.5 rounded-full border border-white/10">
            Refresh
          </button>
          <button
            onClick={csv}
            disabled={!items.length}
            data-testid="resources-export-csv"
            className="text-xs px-3 py-1.5 rounded-full bg-white text-black font-medium hover:bg-slate-200 disabled:opacity-50"
          >
            Export CSV
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-slate-500 text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-slate-500 text-sm">No leads yet. Share the homepage to drive downloads.</div>
      ) : (
        <div className="bg-[#0A0C13] border border-white/10 rounded-2xl overflow-x-auto">
          <div className="min-w-[1020px]">
            <div className="grid grid-cols-14 gap-3 px-6 py-3 border-b border-white/10 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500" style={{gridTemplateColumns:"repeat(14, minmax(0,1fr))"}}>
              <div className="col-span-2">Name</div>
              <div className="col-span-3">Email</div>
              <div className="col-span-2">Resource</div>
              <div className="col-span-2">Source</div>
              <div className="col-span-2">Role / Company</div>
              <div className="col-span-2">Nurture</div>
              <div className="col-span-1 text-right">Date</div>
            </div>
            {items.map((l) => (
              <div
                key={l.id}
                data-testid={`lead-row-${l.id}`}
                className="grid gap-3 px-6 py-3.5 border-b border-white/5 last:border-0 text-sm items-center hover:bg-white/[0.02]"
                style={{gridTemplateColumns:"repeat(14, minmax(0,1fr))"}}
              >
                <div className="col-span-2 text-white truncate">{l.name}</div>
                <div className="col-span-3 text-slate-300 truncate">
                  <a href={`mailto:${l.email}`} className="hover:text-blue-300">{l.email}</a>
                </div>
                <div className="col-span-2">
                  <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-200">
                    {resourceLabel(l.resource)}
                  </span>
                </div>
                <div className="col-span-2 truncate">
                  <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full bg-white/5 border border-white/10 text-slate-300">
                    {l.utm?.source || "direct"}
                  </span>
                  {l.utm?.campaign && (
                    <span className="ml-1 text-[10px] font-mono text-slate-500">/ {l.utm.campaign}</span>
                  )}
                </div>
                <div className="col-span-2 text-slate-400 truncate text-xs">
                  <div>{l.role || "—"}</div>
                  <div className="text-slate-500">{l.company || "—"}</div>
                </div>
                <div className="col-span-2 flex items-center gap-1.5 flex-wrap">
                  <NurtureBadge
                    label="D0"
                    state="sent"
                    onPreview={null}
                    onSend={null}
                  />
                  <NurtureBadge
                    label="D3"
                    state={l.nurture?.day_3_sent ? "sent" : "pending"}
                    onPreview={() => previewNurture(l.id, 3)}
                    onSend={!l.nurture?.day_3_sent ? () => sendNurtureNow(l.id, 3) : null}
                  />
                  <NurtureBadge
                    label="D7"
                    state={l.nurture?.day_7_sent ? "sent" : "pending"}
                    onPreview={() => previewNurture(l.id, 7)}
                    onSend={!l.nurture?.day_7_sent ? () => sendNurtureNow(l.id, 7) : null}
                  />
                </div>
                <div className="col-span-1 text-right flex items-center justify-end gap-2">
                  <span className="font-mono text-[10px] text-slate-500">
                    {new Date(l.created_at).toLocaleDateString()}
                  </span>
                  <button
                    onClick={() => remove(l.id)}
                    className="text-rose-300/70 hover:text-rose-300"
                    aria-label="Delete"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Nurture preview overlay */}
      {preview && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4"
          onClick={() => setPreview(null)}
          data-testid="nurture-preview"
        >
          <div
            className="bg-white text-black max-w-2xl w-full rounded-2xl overflow-hidden max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-black/10 flex items-center justify-between">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                  Nurture preview · Day {preview.day}
                </div>
                <div className="font-display font-semibold text-lg mt-1 truncate">
                  {preview.subject}
                </div>
              </div>
              <button
                onClick={() => setPreview(null)}
                className="text-slate-500 hover:text-black"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-4">
              <iframe
                title="nurture-preview-iframe"
                srcDoc={preview.html}
                className="w-full min-h-[60vh] border border-black/10 rounded-lg bg-white"
              />
            </div>
            <div className="px-6 py-3 border-t border-black/10 flex justify-end">
              <button
                onClick={() => setPreview(null)}
                className="px-4 py-2 rounded-full bg-black text-white text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NurtureBadge({ label, state, onPreview, onSend }) {
  const sent = state === "sent";
  const dot = sent ? "bg-emerald-400" : "bg-amber-400";
  return (
    <div className="inline-flex items-center gap-1.5 group/n">
      <span
        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider border ${
          sent
            ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
            : "bg-white/5 border-white/10 text-slate-400"
        }`}
      >
        <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
        {label}
      </span>
      {onPreview && (
        <button
          onClick={onPreview}
          className="text-[10px] text-slate-500 hover:text-white opacity-0 group-hover/n:opacity-100 transition-opacity"
          title="Preview email"
        >
          ⤴
        </button>
      )}
      {onSend && (
        <button
          onClick={onSend}
          className="text-[10px] text-blue-300/80 hover:text-blue-200 opacity-0 group-hover/n:opacity-100 transition-opacity"
          title="Send now"
        >
          ▶
        </button>
      )}
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div data-testid={testid} className="bg-[#0A0C13] border border-white/10 rounded-2xl p-5">
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">{label}</div>
      <div className="mt-2 font-display font-bold text-3xl text-white tracking-tighter">{value}</div>
    </div>
  );
}

function Breakdown({ label, entries, testid }) {
  const total = entries.reduce((s, [, v]) => s + v, 0) || 1;
  return (
    <div data-testid={testid} className="bg-[#0A0C13] border border-white/10 rounded-2xl p-5">
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">{label}</div>
      <ul className="mt-3 space-y-2">
        {entries.slice(0, 5).map(([k, v]) => (
          <li key={k} className="text-sm">
            <div className="flex items-center justify-between text-slate-200">
              <span className="truncate mr-2">{k}</span>
              <span className="font-mono text-xs text-slate-400">{v}</span>
            </div>
            <div className="mt-1 h-1 rounded-full bg-white/5 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-cyan-400"
                style={{ width: `${(v / total) * 100}%` }}
              />
            </div>
          </li>
        ))}
        {entries.length === 0 && <li className="text-xs text-slate-500">No data yet</li>}
      </ul>
    </div>
  );
}
